import { ILLUSTRATIVE_NOT_ADVICE_DISCLAIMER } from '@cf/shared';
/**
 * Stage 7 — "Explain this year": the human-readable derivation of one
 * projected year, composed purely from the run's calculation trace and year
 * row. Feeds the UI drill-down and the PDF appendix. Wording is illustrative
 * and explanatory — never advice.
 */
function gbp(penceValue) {
    const negative = penceValue < 0;
    const abs = Math.abs(penceValue);
    const pounds = Math.trunc(abs / 100).toLocaleString('en-GB');
    const rem = (abs % 100).toString().padStart(2, '0');
    // ASCII hyphen-minus, matching GRAPH_REPORT — survives every PDF font.
    return `${negative ? '-' : ''}£${pounds}.${rem}`;
}
/** Lay names for the marginal band; 'none' is suppressed by the caller.
 * Exhaustive over the band union so a new band fails the type-check here,
 * never as a machine code in client-facing text. */
const BAND_NAMES = {
    basic: 'basic rate',
    higher: 'higher rate',
    additional: 'additional rate',
};
/** Exhaustive over Wrapper: adding a wrapper without a lay name is a
 * type-check failure, not a machine code leaking into a client's reading. */
const WRAPPER_NAMES = {
    cash: 'Cash',
    cash_isa: 'Cash ISA',
    stocks_isa: 'Stocks & shares ISA',
    lisa: 'Lifetime ISA',
    jisa: 'Junior ISA',
    nsi: 'NS&I premium bonds',
    gia: 'General investment account',
    dc_pension: 'Pension (defined contribution)',
    db_pension: 'Pension (defined benefit)',
    state_pension: 'State pension',
    property: 'Property',
    business: 'Business interest',
    other_asset: 'Other asset',
};
function explainPerson(p, lines) {
    const who = p.personId === 'client' ? 'Client' : 'Partner';
    lines.push(`${who}:`);
    if (p.income.length > 0) {
        lines.push('  Income received:');
        for (const line of p.income) {
            lines.push(`    ${line.label}: ${gbp(line.amount)}${line.taxable ? '' : ' (not taxable)'}`);
        }
    }
    else {
        lines.push('  No income received this year.');
    }
    const ti = p.taxableIncome;
    lines.push(`  Taxable income: ${gbp(ti.nonSavings)} general income, ${gbp(ti.savings)} savings interest, ${gbp(ti.dividends)} dividends.`);
    const t = p.incomeTax;
    const bandNote = t.marginalBand !== 'none' ? ` (highest tax band reached: ${BAND_NAMES[t.marginalBand]})` : '';
    lines.push(`  Income tax: personal allowance ${gbp(t.personalAllowance)}; tax on general income ${gbp(t.nonSavingsTax)}, on savings ${gbp(t.savingsTax)}, on dividends ${gbp(t.dividendTax)}${bandNote}.`);
    // Band-by-band derivation — the "how did you get that figure" answer.
    if (t.bands.nonSavings.length > 0) {
        lines.push(`  Tax on general income by band: ${slicesText(t.bands.nonSavings)}.`);
    }
    const sv = t.bands.savings;
    if (sv.atStartingRate > 0 || sv.atPersonalSavingsAllowance > 0 || sv.slices.length > 0) {
        const parts = [
            ...(sv.atStartingRate > 0
                ? [`${gbp(sv.atStartingRate)} within the 0% starting rate for savings`]
                : []),
            ...(sv.atPersonalSavingsAllowance > 0
                ? [`${gbp(sv.atPersonalSavingsAllowance)} within the personal savings allowance (0%)`]
                : []),
            ...(sv.slices.length > 0 ? [slicesText(sv.slices)] : []),
        ];
        lines.push(`  Savings interest taxed as: ${parts.join('; ')}.`);
    }
    const dv = t.bands.dividends;
    if (dv.atDividendAllowance > 0 || dv.slices.length > 0) {
        const parts = [
            ...(dv.atDividendAllowance > 0
                ? [`${gbp(dv.atDividendAllowance)} within the dividend allowance (0%)`]
                : []),
            ...(dv.slices.length > 0 ? [slicesText(dv.slices)] : []),
        ];
        lines.push(`  Dividends taxed as: ${parts.join('; ')}.`);
    }
    if (t.section24Reduction > 0) {
        lines.push(`  Less ${gbp(t.section24Reduction)} basic-rate credit on mortgage interest (the Section 24 rule for landlords).`);
    }
    if (t.annualAllowanceCharge > 0) {
        lines.push(`  Plus ${gbp(t.annualAllowanceCharge)} pension annual-allowance charge (contributions above the allowance).`);
    }
    if (t.unscheduledDrawTax > 0) {
        lines.push(`  Plus ${gbp(t.unscheduledDrawTax)} estimated tax on unplanned pension withdrawals (an estimate — the exact tax would depend on how the withdrawal is actually taken).`);
    }
    lines.push(`  Income tax total: ${gbp(t.total)}. National Insurance: ${gbp(p.nationalInsurance)}.`);
    const cg = p.capitalGains;
    if (cg.standardGains > 0 || cg.residentialGains > 0 || cg.businessGains > 0) {
        lines.push(`  Capital gains realised: ${gbp(cg.standardGains)} standard, ${gbp(cg.residentialGains)} residential property, ${gbp(cg.businessGains)} business — capital gains tax ${gbp(cg.tax)}.`);
    }
    if (p.withdrawals.length > 0) {
        lines.push('  Withdrawals to meet spending:');
        for (const w of p.withdrawals) {
            // Scheduled pension income is taxed through the bands above, not at
            // source — say so, or the draw reads as tax-free.
            const taxNote = w.taxWithheld > 0
                ? ` (of which ${gbp(w.taxWithheld)} estimated tax)`
                : w.method === 'scheduled'
                    ? ' (taxed as income above)'
                    : '';
            lines.push(`    ${WRAPPER_NAMES[w.wrapper]}: ${gbp(w.gross)}${taxNote}`);
        }
    }
    const activePots = p.pots.filter((pot) => pot.opening !== 0 || pot.closing !== 0);
    if (activePots.length > 0) {
        lines.push('  Pots (opening -> closing):');
        for (const pot of activePots)
            lines.push(potLine(pot));
    }
}
/** One pot's year as a lay-readable line; shared by the person and household
 * sections. "Transfers & one-off movements" is the lay gloss for the
 * otherMovements residual (lump sums, purchases, sales, inheritances,
 * transfers on a death). */
function potLine(pot) {
    const parts = [
        ...(pot.contributions > 0 ? [`paid in ${gbp(pot.contributions)}`] : []),
        `assumed growth & income ${gbp(pot.growthAndYield)}`,
        ...(pot.fees > 0 ? [`charges -${gbp(pot.fees)}`] : []),
        ...(pot.withdrawn > 0 ? [`withdrawn ${gbp(pot.withdrawn)}`] : []),
        ...(pot.otherMovements !== 0
            ? [`transfers & one-off movements ${gbp(pot.otherMovements)}`]
            : []),
    ];
    // "->" not U+2192: these lines feed the PDF appendix and must survive
    // constrained fonts (same reasoning as the ASCII minus in gbp()).
    return `    ${WRAPPER_NAMES[pot.wrapper]}: ${gbp(pot.opening)} -> ${gbp(pot.closing)} (${parts.join(', ')})`;
}
/** "£37,700.00 at basic rate (20%) = £7,540.00; …" for a run of band slices. */
function slicesText(slices) {
    return slices
        .map((s) => `${gbp(s.amount)} at ${BAND_NAMES[s.band]} (${rate(s.rateBps)}) = ${gbp(s.tax)}`)
        .join('; ');
}
/** Why a debt line's payment is larger than usual — stated, never inferred.
 * The sale note is deliberately cause-neutral ("when … was sold", not "from
 * the sale"): in negative equity the proceeds do NOT cover the balance — the
 * ordinary deficit machinery funds the gap — so naming the sale as the
 * funding source would be false exactly when an adviser checks hardest. */
export function clearedNote(d) {
    switch (d.cleared) {
        /**
         * A2.5a review round 3. The settled balance used to be pushed onto the
         * year's SPENDING lines while also entering `debtService`, so both the PDF
         * appendix and the year breakdown printed it twice — an itemisation summing
         * to £240,800 against a row total of £140,800 — which is the same
         * "sub-total contradicting its own itemisation" defect the line was added
         * to fix, in the other direction. It belongs among the debt lines, as a
         * child of the total it is part of.
         */
        case 'estateSettlement':
            return ' (the outstanding balance was paid out of the estate at the death modelled in this plan — the money left the plan, it was not written off)';
        case 'finalPayment':
            return ' (includes the last of this debt — it is fully repaid this year)';
        case 'endOfTerm':
            return ' (the remaining balance falls due at the end of its term — it is fully repaid this year)';
        case 'sale':
            return ' (repaid in full when the linked property was sold this year)';
        case 'no':
            return '';
    }
}
/** Basis points as a lay percentage: 2000 → "20%", 3375 → "33.75%". */
function rate(rateBps) {
    const pct = rateBps / 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}
/**
 * The derivation of one year, as plain-English lines. Throws if the year is
 * outside the projected window.
 */
export function explainYear(result, year) {
    const yearTrace = result.trace.find((t) => t.year === year);
    const yearRow = result.years.find((y) => y.year === year);
    if (yearTrace === undefined || yearRow === undefined) {
        throw new RangeError(`Year ${year} is outside the projected window`);
    }
    const lines = [];
    // Name the rule set, not just the year: an adviser reading this aloud to a
    // client must be able to say exactly which published figures produced it.
    // "held constant" heads off the natural "2047/48 under 2026-27 rules — is
    // that a typo?" question: future years reuse the newest reviewed rules.
    lines.push(`Tax year ${yearTrace.taxYearLabel}, modelled under tax rule set ${result.taxRulesVersion} (held constant for every projected year) — ${ILLUSTRATIVE_NOT_ADVICE_DISCLAIMER}`);
    for (const person of yearTrace.people)
        explainPerson(person, lines);
    if (yearTrace.spendingLines.length > 0 || yearRow.debtService > 0) {
        lines.push('Spending this year, item by item:');
        for (const item of yearTrace.spendingLines) {
            lines.push(`  ${item.label}: ${gbp(item.amount)}`);
        }
        // Debt service is part of the household spending headline below; an
        // itemised list that omitted the mortgage would not sum for exactly the
        // households most likely to check it.
        if (yearRow.debtService > 0) {
            lines.push(`  Debt payments (mortgages, loans and other debts): ${gbp(yearRow.debtService)}`);
            // Per-liability lines (3.4.0): the payoff-year spike must explain
            // itself. Older stored traces carry no debtLines — the aggregate line
            // above still stands alone for those.
            for (const d of yearTrace.debtLines ?? []) {
                if (d.paid <= 0)
                    continue;
                lines.push(`    ${d.label}: ${gbp(d.paid)}${clearedNote(d)}`);
            }
        }
    }
    if (yearTrace.lifeEventOutflows.length > 0) {
        lines.push('One-off events paid out this year:');
        for (const item of yearTrace.lifeEventOutflows) {
            lines.push(`  ${item.label}: ${gbp(item.amount)}`);
        }
    }
    if (yearTrace.householdPots.length > 0) {
        lines.push('Household assets (owned outside the personal pots, e.g. by the family or a child):');
        for (const pot of yearTrace.householdPots)
            lines.push(potLine(pot));
    }
    lines.push(`Household: spending ${gbp(yearRow.spending)} (including debt payments ${gbp(yearRow.debtService)}); total tax ${gbp(yearRow.totalTax)}; net cash flow ${gbp(yearRow.netCashFlow)}.`);
    if (yearRow.unmetShortfall > 0) {
        lines.push(`Shortfall: ${gbp(yearRow.unmetShortfall)} of spending could not be met from any modelled source this year.`);
    }
    // UX-08: two year-end figures, each under its own name. The table's "closing
    // net worth" deducts spending the plan could not fund; this line used to
    // call the undeducted figure "net worth at year end" beside it.
    lines.push(`Assets less debts at year end: ${gbp(yearRow.totalNetWorth)} (assets ${gbp(yearRow.audit.closingAssets)} minus debts ${gbp(yearRow.liabilitiesOutstanding)}).`);
    if (yearRow.netWorthAfterShortfall !== yearRow.totalNetWorth) {
        lines.push(`Net worth after spending the plan could not fund, to date: ${gbp(yearRow.netWorthAfterShortfall)}.`);
    }
    const yearWarnings = result.warnings.filter((w) => w.year === year);
    for (const w of yearWarnings)
        lines.push(`Note: ${w.message}`);
    return lines;
}
//# sourceMappingURL=explain.js.map